import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, MapPin, Globe, Twitter, Facebook, Instagram, Linkedin, Mail, TrendingUp, Eye, Share2, MessageCircle, Calendar } from 'lucide-react';

const JournalistProfilePage = () => {
  const { username } = useParams();
  const [journalist, setJournalist] = useState(null);
  const [articles, setArticles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('articles'); // 'articles' or 'about'

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadJournalistData();
  }, [username]);

  const loadJournalistData = async () => {
    try {
      setLoading(true);
      
      // Load journalist profile
      const profileRes = await fetch(`${backendUrl}/api/journalists/${username}`);
      if (!profileRes.ok) throw new Error('Journalist not found');
      const profileData = await profileRes.json();
      setJournalist(profileData);

      // Load journalist articles
      const articlesRes = await fetch(`${backendUrl}/api/journalists/${username}/articles`);
      const articlesData = await articlesRes.json();
      setArticles(articlesData);

      // Load analytics
      const analyticsRes = await fetch(`${backendUrl}/api/journalists/${username}/analytics`);
      const analyticsData = await analyticsRes.json();
      setAnalytics(analyticsData);

    } catch (error) {
      console.error('Error loading journalist:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">Loading profile...</p>
      </div>
    );
  }

  if (!journalist) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold mb-4">Journalist Not Found</h1>
        <Link to="/" className="text-blue-600 hover:underline">Return to Homepage</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Profile Photo */}
          <div className="flex-shrink-0">
            {journalist.profile_photo ? (
              <img
                src={journalist.profile_photo}
                alt={journalist.display_name}
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-100"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-100">
                <User size={48} className="text-gray-400" />
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-grow">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">{journalist.display_name}</h1>
              {journalist.is_verified && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  ✓ Verified
                </span>
              )}
            </div>
            
            {journalist.location && (
              <div className="flex items-center gap-2 text-gray-600 mb-3">
                <MapPin size={16} />
                <span>{journalist.location}</span>
              </div>
            )}

            {journalist.bio && (
              <p className="text-gray-700 mb-4 max-w-2xl">{journalist.bio}</p>
            )}

            {/* Specialties */}
            {journalist.specialties && journalist.specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {journalist.specialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            )}

            {/* Social Links */}
            <div className="flex flex-wrap gap-3">
              {journalist.website && (
                <a
                  href={journalist.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Globe size={18} />
                  <span className="text-sm">Website</span>
                </a>
              )}
              {journalist.twitter && (
                <a
                  href={`https://twitter.com/${journalist.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-600 hover:text-blue-400 transition-colors"
                >
                  <Twitter size={18} />
                  <span className="text-sm">@{journalist.twitter}</span>
                </a>
              )}
              {journalist.facebook && (
                <a
                  href={`https://facebook.com/${journalist.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Facebook size={18} />
                  <span className="text-sm">Facebook</span>
                </a>
              )}
              {journalist.instagram && (
                <a
                  href={`https://instagram.com/${journalist.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-600 hover:text-pink-600 transition-colors"
                >
                  <Instagram size={18} />
                  <span className="text-sm">@{journalist.instagram}</span>
                </a>
              )}
              {journalist.linkedin && (
                <a
                  href={journalist.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-600 hover:text-blue-700 transition-colors"
                >
                  <Linkedin size={18} />
                  <span className="text-sm">LinkedIn</span>
                </a>
              )}
              {journalist.email && (
                <a
                  href={`mailto:${journalist.email}`}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Mail size={18} />
                  <span className="text-sm">Email</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <TrendingUp size={18} />
              <span className="text-sm font-medium">Articles</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{analytics.total_articles}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Eye size={18} />
              <span className="text-sm font-medium">Total Views</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.total_views)}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Share2 size={18} />
              <span className="text-sm font-medium">Shares</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.total_shares)}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <MessageCircle size={18} />
              <span className="text-sm font-medium">Comments</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(analytics.total_comments)}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('articles')}
            className={`pb-3 px-2 font-medium transition-colors ${
              activeTab === 'articles'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Articles ({articles.length})
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`pb-3 px-2 font-medium transition-colors ${
              activeTab === 'about'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Analytics
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'articles' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <Link
              key={article._id}
              to={`/article/${article.slug}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {article.image && (
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <div className="text-xs text-gray-500 uppercase mb-2">
                  {article.category}
                </div>
                <h3 className="font-bold text-lg mb-2 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar size={14} />
                  <span>{formatDate(article.published_date)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {activeTab === 'about' && analytics && (
        <div className="space-y-6">
          {/* Top Articles */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold mb-4">Top Performing Articles</h2>
            <div className="space-y-4">
              {analytics.top_articles.map((article, index) => (
                <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-grow">
                    <Link
                      to={`/article/${article.slug}`}
                      className="font-medium text-gray-900 hover:text-blue-600 mb-1 block"
                    >
                      {article.title}
                    </Link>
                    <div className="flex gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Eye size={14} />
                        {formatNumber(article.views)} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Share2 size={14} />
                        {article.shares} shares
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle size={14} />
                        {article.comments} comments
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Engagement Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold mb-4">Engagement Metrics</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Engagement Rate</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.engagement_rate}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg. Read Time</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.avg_read_time} min</p>
              </div>
              {Object.keys(analytics.shares_by_platform).length > 0 && (
                <div className="col-span-2 md:col-span-1">
                  <p className="text-sm text-gray-600 mb-2">Shares by Platform</p>
                  <div className="space-y-1">
                    {Object.entries(analytics.shares_by_platform).map(([platform, count]) => (
                      <div key={platform} className="flex justify-between text-sm">
                        <span className="capitalize text-gray-700">{platform}</span>
                        <span className="font-medium text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalistProfilePage;
